import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  viewChild,
  ViewChild,
} from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { debounceTime, first, map, startWith, take, tap } from 'rxjs/operators';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SnippetsViewModel } from 'src/app/models/snippets-view.model';
import { SnippetService } from 'src/app/services/snippet-service';
import { GetSnippetsRequest } from 'src/app/models/snippet-requests.model';
import { DocumentDataService } from 'src/app/services/document-data.service';
import { NgbDropdown, NgbDropdownMenu } from '@ng-bootstrap/ng-bootstrap';
import { TextControlService } from 'src/app/services/text-control.service';
import { TextControlContext, Selection } from 'textcontrol-promises-develop';
import { DialogService } from 'src/app/services/dialog.service';
import { SnippetAddDialogComponent } from '../snippet-add-dialog/snippet-add-dialog.component';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { ToastService } from 'src/app/services/toast.service';
import { LoadingService } from 'src/app/services/loading.service';
import { LoadingAction } from 'src/app/models/loading-action.model';
import { FormBuilder, FormGroup, NgForm } from '@angular/forms';

interface SnippetWithUIState extends SnippetsViewModel {
  sanitizedSnippetBody: SafeHtml;
}

declare const TXTextControl: any;
declare const Uint8Array: any;

@Component({
  selector: 'tx-snippet-list-dialog',
  templateUrl: './snippet-list-dialog.component.html',
  styleUrls: ['./snippet-list-dialog.component.scss'],
})
export class SnippetListDialogComponent implements OnInit, AfterViewInit {
  @Input() menuInstance!: NgbDropdown;
  @ViewChild('snippetTitleElement') snippetTitleElement!: ElementRef;
  @ViewChild('filterThisFormElement') filterThisFormElement!: ElementRef;
  public filterForm!: FormGroup;
  public loggedInStaffId?: string;
  public activeTemplateSysId: number = 0; // Set your activeTemplateSysId here
  filterText = '';
  filterText$ = new BehaviorSubject<string>('');
  filterGeneralSnippets$ = new BehaviorSubject<boolean>(false);
  filterUserSnippets$ = new BehaviorSubject<boolean>(true);
  filterOnlyFormSnippets$ = new BehaviorSubject<boolean>(true);
  filteredSnippets$?: Observable<SnippetWithUIState[]>;
  praSysId: number | undefined;
  constructor(
    private snippetService: SnippetService,
    private sanitizer: DomSanitizer,
    public documentDataService: DocumentDataService,
    public textControlService: TextControlService,
    private dialogService: DialogService,
    private authenticationService: AuthenticationService,
    private toastService: ToastService,
    private loadingService: LoadingService,
    private changeDetectorRef: ChangeDetectorRef,
    private formBuilder: FormBuilder
  ) {}

  ngOnInit(): void {
    let details = this.authenticationService.getUserDetails();
    this.loggedInStaffId = details.StaffGuid;
    this.activeTemplateSysId = this.documentDataService.getTemplateId() || 0;
    this.praSysId = this.documentDataService.getPraSysId();
    this.filterForm = this.formBuilder.group({
      filterThisForm: [{ value: !!this.activeTemplateSysId, disabled: !this.activeTemplateSysId }],
      filterPracticeArea: [{ value: !this.activeTemplateSysId, disabled: !this.activeTemplateSysId }],
      firmLevel: [false],
      filterGeneralSnippets: [!this.activeTemplateSysId],
    });
    this.filteredSnippets$ = combineLatest([
      this.snippetService.snippets$,
      this.filterText$.pipe(debounceTime(300), startWith('')),
      this.filterGeneralSnippets$.pipe(startWith(this.filterForm.get('filterGeneralSnippets')?.value)),
      this.filterUserSnippets$.pipe(startWith(this.filterForm.get('firmLevel')?.value)),
      this.filterOnlyFormSnippets$.pipe(startWith(this.filterForm.get('filterThisForm')?.value)),
    ]).pipe(
      map(([snippets, filterText, includeGeneral, onlyMine, onlyActiveForm]) => {
        const sanitizedSnippets = this.sanitizeSnippets(snippets);
        return sanitizedSnippets.filter((snippet) => {
          let matchesFilter = true;
          if (filterText) {
            const lowerFilterText = filterText.toLowerCase();
            this.filterText = lowerFilterText;
            const snipNote = snippet.snippetNote ? snippet.snippetNote.toLowerCase() : '';
            const snipTitle = snippet.snippetTitle ? snippet.snippetTitle.toLowerCase() : '';
            const snipBody = snippet.snippetBodyPlainText ? snippet.snippetBodyPlainText.toLowerCase() : '';
            matchesFilter =
              snipNote.includes(lowerFilterText) ||
              snipTitle.includes(lowerFilterText) ||
              snipBody.includes(lowerFilterText);
            //this.changeDetectorRef.detectChanges();
          } else {
            this.filterText = '';
          }

          const returnValue =
            matchesFilter &&
            (onlyMine
              ? snippet.staffId === this.loggedInStaffId
              : snippet.sharedSnippet || snippet.staffId === this.loggedInStaffId) &&
            (onlyActiveForm
              ? snippet.templateSysId === this.activeTemplateSysId || (includeGeneral && snippet.praSysId == 0)
              : (snippet.praSysId == this.praSysId && this.praSysId != 0) || (includeGeneral && snippet.praSysId == 0));
          return returnValue;
        });
      })
    );
    this.refreshSnippets();
  }

  ngAfterViewInit(): void {}

  private sanitizeSnippets(snippets: SnippetsViewModel[]): SnippetWithUIState[] {
    const sanitizedSnippets = snippets.map((snippet) => ({
      ...snippet,
      sanitizedSnippetBody: this.sanitizer.bypassSecurityTrustHtml(snippet.snippetBody),
    }));

    return sanitizedSnippets;
  }

  public refreshSnippets(force = false) {
    const praSysId = this.documentDataService.getPraSysId();
    const request: GetSnippetsRequest = { praSysId: praSysId };
    this.snippetService.getSnippets(request, force);
  }

  onFilterTextChanged(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    if (inputElement) {
      this.filterText$.next(inputElement.value);
    }
  }

  onGeneralSnippetsChanged(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    if (inputElement) {
      this.filterGeneralSnippets$.next(inputElement.checked);
    }
  }

  onFirmLevelChanged(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    if (inputElement) {
      this.filterUserSnippets$.next(!inputElement.checked);
    }
  }

  onAllForPracticeAreaChanged(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.filterForm.get('filterThisForm')?.setValue(!inputElement.checked);
    if (inputElement) {
      this.filterOnlyFormSnippets$.next(!inputElement.checked);
    }
  }

  onOnlyFormChanged(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.filterForm.get('filterPracticeArea')?.setValue(!inputElement.checked);
    if (inputElement) {
      this.filterOnlyFormSnippets$.next(inputElement.checked);
    }
  }

  async onSnippetClick(snippet: any) {
    let existingCharBefore = '';
    let existingCharAfter = '';
    const startPosition = this.textControlService.currentSelection().start;
    const textPartLength = this.textControlService.currentTextPartLength();
    const isNotEmpty = textPartLength !== 0;
    const isAtStart = startPosition === 0;
    const isAtEnd = startPosition === textPartLength;

    if (isNotEmpty) {
      try {
        if (!isAtStart && !isAtEnd) {
          // Not at the beginning or end of the text part
          existingCharBefore = await this.textControlService.getCharacterAtLocationAsPromise(startPosition - 1);
          existingCharAfter = await this.textControlService.getCharacterAtLocationAsPromise(startPosition);
        } else if (isAtStart) {
          // At the beginning of the text part
          existingCharAfter = await this.textControlService.getCharacterAtLocationAsPromise(startPosition);
        } else if (isAtEnd) {
          // At the end of the text part
          existingCharBefore = await this.textControlService.getCharacterAtLocationAsPromise(startPosition - 1);
        }
      } catch (error) {
        console.error('Error fetching characters at location:', error);
        return;
      }
    }

    let loadSettings = {
      addParagraph: false,
      applicationFieldFormat: TXTextControl.ApplicationFieldFormat.None,
      loadDocumentBackColor: false,
      loadHypertextLinks: false,
      loadImages: false,
      loadSubTextParts: false,
    };
    let savedStyle: any = null;
    try {
      savedStyle = await this.textControlService.getCurrentStyleAsPromise();
    } catch (error) {
      console.error('Error fetching current style:', error);
      this.toastService.show('Failed to identify the current style in the document.', {
        classname: 'bg-danger',
        autohide: false,
      });
      return;
    }
    this.loadingService.setLoadingAction(LoadingAction.Updating);
    this.menuInstance.close();
    this.changeDetectorRef.detectChanges();
    TXTextControl.beginUndoAction('InsertSnippet', async () => {
      try {
        this.snippetService.logSnippetUsage({
          Location: startPosition,
          PraSysId: this.praSysId ?? 0,
          StaffId: this.loggedInStaffId ?? '',
          TemplateSysId: this.activeTemplateSysId,
          SnippetGuid: snippet.snippetGuid,
          SnippetId: snippet.snippetId ?? 0,
        });
        loadSettings.addParagraph = false;
        // await this.loadHtmlIntoTextControlAsPromise(snippet.snippetBody, loadSettings);
        const txContext = new TextControlContext();
        const parser = new DOMParser();
        const doc = parser.parseFromString(snippet.snippetBody, 'text/html');
        const spans = doc.querySelectorAll('span');
        spans.forEach((span) => {
          if (!span.hasAttribute('class') && !span.hasAttribute('style')) {
            // Move children to the parent of the span
            while (span.firstChild) {
              span?.parentNode?.insertBefore(span.firstChild, span);
            }
            // Remove the span element
            span.remove();
          }
        });

        // Create an empty document fragment to build new structure
        const fragment = document.createDocumentFragment();
        let paragraphContent = '';
        let oldStartPosition = await txContext.selection.getStart();

        // Traverse the document and replace <br> tags with <p> structure
        const promises = new Array<Promise<void>>();
        doc.body.childNodes.forEach((node: any) =>
          this.processNodeRecursively(node, txContext, this.textControlService, promises)
        );
        await Promise.all(promises);

        let newPosition = await txContext.selection.getStart();
        let newContentLength = newPosition - startPosition;

        //check the character after the snippet first to maintain position easier
        if (existingCharBefore != ' ') {
          await txContext.selection.setStart(startPosition);
          await txContext.selection.setLength(0);
          await txContext.selection.setText(' ');
          newContentLength++;
        }

        if (existingCharAfter != ' ') {
          // if the character before the snippet is not a space, add a space before the snippet
          await txContext.selection.setStart(startPosition + newContentLength);
          await txContext.selection.setLength(0);
          await txContext.selection.setText(' ');
          newContentLength++;
        }
        await txContext.selection.setStart(startPosition);
        await txContext.selection.setLength(newContentLength);
        //await this.textControlService.setCurrentStyleAsPromise(savedStyle);
      } catch (error) {
        console.error('Error Inserting current snippet:', error);
        this.toastService.show('There was a problem inserting the selected snippet.', {
          classname: 'bg-danger',
          autohide: false,
        });
        return;
      } finally {
        TXTextControl.endUndoAction();
        TXTextControl.focus();
        this.loadingService.resetLoadingDocumentIndicator();
      }
    });
  }
  async processNodeRecursively(node: any, txContext: any, textControlService: any, promises: any[]) {
    if (!node) return;

    // Handle specific node types and styles
    if (node.nodeName === 'BR') {
      promises.push(txContext.selection.setText('\n'));
      return;
    }
    if ((node.nodeName === 'SPAN' || node.nodeName === 'DIV') && node.style.textDecoration === 'underline') {
      promises.push(textControlService.setUnderlineAsync(TXTextControl.FontUnderlineStyle.Single));
    }
    if (node.nodeName === 'STRONG' || (node.style && node.style.fontWeight === 'bold')) {
      promises.push(textControlService.setBoldAsync(true));
    }
    if (node.nodeName === 'EM' || (node.style && node.style.fontStyle === 'italic')) {
      promises.push(textControlService.setItalicAsync(true));
    }

    if (node.nodeName === '#text') {
      // Set text content
      const text = node.textContent || (node as HTMLElement).innerHTML;
      promises.push(txContext.selection.setText(text || ''));
    }

    // Process child nodes recursively
    if (node.childNodes && node.childNodes.length > 0) {
      node.childNodes.forEach((childNode: any) =>
        this.processNodeRecursively(childNode, txContext, textControlService, promises)
      );
    }

    // Reset styles after processing node
    if ((node.nodeName === 'SPAN' || node.nodeName === 'DIV') && node.style.textDecoration === 'underline') {
      promises.push(textControlService.setUnderlineAsync(TXTextControl.FontUnderlineStyle.None));
    }
    if (node.nodeName === 'STRONG' || (node.style && node.style.fontWeight === 'bold')) {
      promises.push(textControlService.setBoldAsync(false));
    }
    if (node.nodeName === 'EM' || (node.style && node.style.fontStyle === 'italic')) {
      promises.push(textControlService.setItalicAsync(false));
    }
  }

  getHighlighterClass(): string {
    return 'highlighter';
  }

  deleteSnippetClick(snippet: SnippetWithUIState, event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.dialogService.confirm({
      title: 'Delete Snippet',
      message: 'Are you sure you want to delete this snippet?',
      confirmCallback: () => {
        let deleteRequest = {
          snippetGuid: snippet.snippetGuid,
          praSysId: snippet.praSysId ?? 0,
        };
        this.snippetService
          .deleteSnippet(deleteRequest)
          .pipe(
            // map((snippets) => this.sanitizeSnippets(snippets))
          )
          .subscribe((snippets) => {
            setTimeout(() => {
              this.toastService.show('Snippet has been deleted.', {
                classname: 'bg-warning',
                autohide: true,
              });
            }, 50);
          });
      },
      rejectCallback: () => {},
    });
  }

  copySnippetClick(snippet: SnippetWithUIState, $event: MouseEvent) {
    let newSnippet = { ...snippet };
    newSnippet.snippetGuid = '';
    newSnippet.snippetTitle = newSnippet.snippetTitle + ' (Copy)';
    this.editSnippetClick(newSnippet, $event);
  }

  editSnippetClick(snippet: SnippetWithUIState, event: Event) {
    event.stopPropagation();
    event.preventDefault();
    const modalRef = this.dialogService.openCustomDialog('addSnippet', SnippetAddDialogComponent, {
      size: 'md',
      backdrop: 'static',
    });
    const modalInstance = modalRef.componentInstance;
    modalInstance.SnippetModel = { ...snippet };

    modalRef.result.then((res) => {
      this.refreshSnippets(true);
      //Done Saved in Modal...
    });
  }

  infoSnippetClick(snippet: SnippetWithUIState, event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
  }

  prepareForImport(html: string): { firstLine: string; body: string; lastLine: string } {
    // Parse the HTML string into a document
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Create an empty document fragment to build new structure
    const fragment = document.createDocumentFragment();
    const paragraphs: string[] = [];
    let paragraphContent = '';
    // Traverse the document and replace <br> tags with <p> structure
    doc.body.childNodes.forEach((node) => {
      if (node.nodeName === 'BR') {
        paragraphs.push(paragraphContent);
        paragraphContent = '';
      } else if (
        node.nodeType === Node.TEXT_NODE ||
        node.nodeName === 'SPAN' ||
        node.nodeName === 'DIV' ||
        node.nodeName === 'STRONG' ||
        node.nodeName === 'EM'
      ) {
        paragraphContent += (node as HTMLElement).outerHTML || node.textContent;
      }
    });

    // Push any remaining content as a paragraph
    paragraphs.push(paragraphContent.trim());

    // Convert paragraphs array into <p> elements and append to the fragment
    paragraphs.forEach((paragraph) => {
      const p = document.createElement('p');
      p.innerHTML = paragraph;
      fragment.appendChild(p);
    });

    // Clear the body and append new structure
    doc.body.innerHTML = '';
    doc.body.appendChild(fragment);
    doc.body.firstElementChild?.innerHTML;
    let returnVal = {
      firstLine: doc.body.firstElementChild?.innerHTML.trimStart() ?? '',
      body: '',
      lastLine:
        doc.body.firstElementChild != doc.body.lastElementChild
          ? doc.body.lastElementChild?.innerHTML.trimEnd() ?? ''
          : '',
    };
    // Return the modified HTML as a string
    doc.body.firstElementChild?.remove();
    doc.body.lastElementChild?.remove();
    returnVal.body = doc.body.innerHTML;
    return returnVal;
  }

  loadHtmlIntoTextControlAsPromise(html: string, loadSettings: any) {
    const base64 = this.bytesToBase64(new TextEncoder().encode(html));
    return new Promise<void>((resolve, reject) => {
      TXTextControl.selection.load(TXTextControl.StreamType.HTMLFormat, base64, resolve(), loadSettings, (err: any) => {
        // error callback
        console.error('Error when loading snippetHMTL', err);
        reject(err);
      });
    });
  }
  getFormattedNote(snippetNote: string): string {
    return snippetNote.replace(/\n/g, '<br>') || '';
  }

  containsText(originalText: string, textToFind: string): boolean {
    if (typeof originalText !== 'string' || !textToFind) {
      return false;
    }

    const pattern = textToFind
      .replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&')
      .split(' ')
      .filter((t) => t.length > 0)
      .join('|');

    const regex = new RegExp(pattern, 'gi');
    return regex.test(originalText) === true;
  }

  base64ToBytes(base64: string) {
    const binString = atob(base64);
    return Uint8Array.from(binString, (m: any) => m.codePointAt(0));
  }

  bytesToBase64(bytes: any) {
    const binString = Array.from(bytes, (byte: any) => String.fromCodePoint(byte)).join('');
    return btoa(binString);
  }
}
